/* ============================================================================
   MEMORA FEATURE BRIDGE

   Secondary product surfaces (Ask Memora / Learn, quiz generation and import,
   question regeneration, Calendar, Clinic, source compression) load on intent
   through MemoraRuntime.ensureFeature instead of on every page load. They are
   still classic scripts in the same flat global scope; only the moment they
   evaluate has changed.

   This file is the eager half of that split and owns three things:

   1. State that is shared across the split. A `let`/`const` binding declared in
      a lazily loaded module would not exist until that module evaluates, so any
      binding the eager bundle touches is declared here instead and the lazy
      module keeps only its assignments.
   2. Call-through stubs. Every entry point the eager bundle or an inline
      handler in index.html can reach is published as a window property before
      its module exists. A `function` declaration in the loaded module replaces
      that property, so a call made after the load runs the real implementation.
      Stubs only exist for call sites that are NOT already guarded by a
      `typeof x === 'function'` fallback; adding one where a guarded fallback
      exists would silently replace that fallback.
   3. Intent triggers for surfaces whose own listeners live inside a lazy
      module, plus an idle prefetch so a deferred module is usually already
      resident by the time it is asked for.

   Load order: after `js/modules/app.icons.js`, before `js/modules/app.core.js`.
   ============================================================================ */

// Capture arrival before core creates the first session on a new installation.
window.memoraCompanionReturningVisit = (() => {
    try {
        if (new URLSearchParams(window.location.search).get('demo') === '1') return false;
        const returning = !!(window.localStorage.getItem('lastSessionId') || window.localStorage.getItem('memora_companion_visited_v1'));
        window.localStorage.setItem('memora_companion_visited_v1', '1');
        return returning;
    } catch (_) { return false; }
})();

/* --- 1. State shared across the eager/lazy split ------------------------- */

/* Model identifiers. Declared here because the eager bundle builds model
   ladders with them and Calendar, Clinic and Study Guide read them while they
   evaluate. app.ai.js keeps every other Gemini constant. */
// Legacy 2.x Flash preferences are mapped to current routes by app.ai.js.
const GEMINI_38_FLASH_MODEL = 'gemini-3.8-flash';
const GEMINI_37_FLASH_MODEL = 'gemini-3.7-flash';
const GEMINI_36_FLASH_MODEL = 'gemini-3.6-flash';
const GEMINI_35_FLASH_MODEL = 'gemini-3.5-flash';
const GEMINI_3_FLASH_MODEL = 'gemini-3-flash-preview';
const GEMINI_35_FLASH_LITE_MODEL = 'gemini-3.5-flash-lite';

/* Fast Mode is an app-wide generation preference. It lives in the eager bridge
   so every lazy feature sees the same arbitration point, while capability
   exceptions remain explicit at the resolver boundary. */
const MEMORA_FAST_MODEL_MODE_KEY = 'memora_fast_model_mode_v1';
const MEMORA_FAST_MODEL_MODE_EXEMPT_CAPABILITIES = Object.freeze(new Set([
    'embedding',
    'embedcontent',
    'batchembedcontents',
    'live',
    'liveaudio',
    'live.connect',
    'tts',
    'texttospeech',
    'grounded-double-check',
    'imagen',
    'imagen.predict',
    'predict'
]));

function getMemoraFastModelModePreference() {
    try {
        // Fast Mode (3.5 Flash Lite) is the default; only an explicit Off turns it off.
        return localStorage.getItem(MEMORA_FAST_MODEL_MODE_KEY) !== 'false';
    } catch (_err) {
        return false;
    }
}

function isMemoraFastModelModeEnabled() {
    if (typeof document !== 'undefined'
        && document.documentElement?.classList?.contains('phone-device')) {
        return false;
    }
    return getMemoraFastModelModePreference();
}

function resolveMemoraGenerationModelLadder(modelList = [], options = {}) {
    const models = Array.from(new Set(
        (Array.isArray(modelList) ? modelList : [])
            .map((modelName) => String(modelName || '').trim())
            .map((modelName) => typeof normalizeGeminiModelAlias === 'function'
                ? normalizeGeminiModelAlias(modelName) : modelName)
            .filter((modelName) => !/^gemini-2\.(?:0|5)-flash(?:-lite)?$/i.test(modelName))
            .filter(Boolean)
    ));
    if (!models.length) return models;

    const capability = String(options?.capability || options?.action || 'generateContent')
        .trim()
        .toLowerCase();
    const isExempt = MEMORA_FAST_MODEL_MODE_EXEMPT_CAPABILITIES.has(capability)
        || capability.startsWith('live.')
        || capability.startsWith('imagen:');
    /* The anonymous Tutor/Study Guide path uses the same shared free-tier
       generation route as the Learn relay contract. Quiz Answer Enrichment
       remains capability-scoped elsewhere because it intentionally uses its
       own 3.1 Flash Lite route. */
    if (isExempt) return models;
    if (options?.anonymousFreeTier === true) return [GEMINI_35_FLASH_LITE_MODEL];
    if (!isMemoraFastModelModeEnabled()) return models;

    // Fast Mode is a model lock for ordinary generation. A full-size fallback after
    // one Lite failure makes a Tutor or quiz run slow despite the user's choice.
    return [GEMINI_35_FLASH_LITE_MODEL];
}

/* Owned by app.ai.js at runtime; declared here so the eager bundle can read,
   reset and clear them before Ask Memora has loaded. app.ai.js assigns
   aiResponseCache its real shape as soon as it evaluates. */
let aiResponseCache = null;
let lastUsedModel = '';
let lastAIResponseData = { text: '', label: '', model: '', modelRaw: '' };
const pendingAIRequests = new Map();

/* Owned by app.quizgen.js at runtime; the eager bundle clears PDF text caches
   on session teardown and reads generation progress while drawing connectors. */
let currentPdfText = null; // Legacy single-file cache
let currentPdfTextKey = null;
let pdfTextCache = {}; // Global cache: filename -> text
let quizGenBatchProgressState = null;

/* Users without a personal Google Gemini API key can generate quizzes and use Ask through the relay.
   Other modules require that key. Sample demos stay unrestricted and use in-memory fixtures.
   Every launch and restored route reads this eager predicate. */
const MEMORA_PRODUCT_FEATURES = Object.freeze({
    ask: 'free',
    quizzes: 'free',
    'game-mode': 'restricted',
    'study-guide': 'restricted',
    'guided-tutor': 'restricted',
    flashcards: 'restricted',
    occlusion: 'restricted',
    clinic: 'restricted',
    'lecture-recorder': 'restricted',
    'voice-agent': 'restricted',
    // AI side doors outside the Learn modules. Each calls the model on its own, so each needs
    // its own gate rather than inheriting one from a module launch.
    calendar: 'restricted',
    'study-plan': 'restricted',
    'smart-evidence': 'restricted'
});
const MEMORA_PRODUCT_FEATURE_LABELS = Object.freeze({
    'game-mode': 'Game Mode',
    calendar: 'Calendar import',
    'study-plan': 'AI Study Plans',
    'smart-evidence': 'AI Smart Evidence',
    'study-guide': 'Study Guide',
    'guided-tutor': 'Guided Tutor',
    flashcards: 'Flashcards',
    occlusion: 'Closed Occlusion',
    clinic: 'Clinic',
    'lecture-recorder': 'Lecture Recorder',
    'voice-agent': 'Voice'
});
const MEMORA_LEARN_MODE_FEATURES = Object.freeze({
    'learn-quizzes': 'quizzes',
    'learn-guide': 'study-guide',
    'learn-guided': 'guided-tutor',
    'learn-flashcards': 'flashcards',
    'learn-occlusion': 'occlusion',
    'learn-recorder': 'lecture-recorder',
    'learn-clinic': 'clinic'
});

function isMemoraFreeTierLimitActive() {
    if (window.PUBLIC_APP_CONFIG?.productAccess?.freeTierLimit !== true) return false;
    if (typeof IS_EMBED_DEMO !== 'undefined' && IS_EMBED_DEMO) return false;
    const key = String(window.getUserCustomGeminiAPIKey?.() || '').trim();
    return !key || key === '__memora_relay__';
}

/* An unknown feature is refused while the limit is on: a new module has to be placed on one side
   of the policy before a free profile can reach it. */
function isMemoraProductFeatureAllowed(feature) {
    if (!isMemoraFreeTierLimitActive()) return true;
    return MEMORA_PRODUCT_FEATURES[String(feature || '')] === 'free';
}

function isMemoraLearnModeAllowed(mode) {
    return isMemoraProductFeatureAllowed(MEMORA_LEARN_MODE_FEATURES[String(mode || '')] || '');
}

function requireMemoraLearnMode(mode) {
    return requireMemoraProductFeature(MEMORA_LEARN_MODE_FEATURES[String(mode || '')] || '');
}

function getMemoraProductFeatureAccessMessage(feature) {
    const label = MEMORA_PRODUCT_FEATURE_LABELS[String(feature || '')] || 'this feature';
    return `Add your own free Google Gemini API key in Settings → AI to use ${label}.`;
}

function getMemoraLearnModeAccessMessage(mode) {
    return getMemoraProductFeatureAccessMessage(MEMORA_LEARN_MODE_FEATURES[String(mode || '')] || '');
}

/* The one refusal every gated entry point uses: it starts nothing and returns false so the
   caller stops where it is. The reader gets the same centred popup as the daily limit, with
   Show me opening the existing key setup. One popup at a time, however many clicks. */
function requireMemoraProductFeature(feature) {
    if (isMemoraProductFeatureAllowed(feature)) return true;
    void showMemoraProductFeatureLockedNotice(feature);
    return false;
}

let memoraLockedFeatureNoticeOpen = false;

async function showMemoraProductFeatureLockedNotice(feature) {
    const label = MEMORA_PRODUCT_FEATURE_LABELS[String(feature || '')] || 'this feature';
    const isLearnModule = Object.values(MEMORA_LEARN_MODE_FEATURES).includes(String(feature || ''));
    const rest = feature === 'voice-agent'
        ? 'every Learn module'
        : (isLearnModule ? 'every other Learn module and Voice' : 'every Learn module and Voice');
    if (typeof showThemeConfirm !== 'function') {
        if (typeof showPopup === 'function') showPopup(getMemoraProductFeatureAccessMessage(feature), null, true);
        return false;
    }
    if (memoraLockedFeatureNoticeOpen) return false;
    memoraLockedFeatureNoticeOpen = true;
    const dismissOnNavigation = () => resolveThemeConfirm(false);
    window.addEventListener('memora:feature-opened', dismissOnNavigation);
    try {
        const confirmed = await showThemeConfirm(
            `Your own free Google Gemini API key unlocks ${label} plus ${rest}, without the free daily limits. It takes about 10 seconds.`,
            { title: `Unlock ${label}`, okText: 'Show me', linkOk: true, hideCancel: true, navigationDismissOnly: true, showClose: true, popIn: true }
        );
        if (confirmed) window.openAPIKeyModal?.({ finalStep: true, source: 'locked-feature', feature });
        return confirmed;
    } finally {
        window.removeEventListener('memora:feature-opened', dismissOnNavigation);
        memoraLockedFeatureNoticeOpen = false;
    }
}

window.isMemoraFreeTierLimitActive = isMemoraFreeTierLimitActive;
window.isMemoraProductFeatureAllowed = isMemoraProductFeatureAllowed;
window.isMemoraLearnModeAllowed = isMemoraLearnModeAllowed;
window.requireMemoraProductFeature = requireMemoraProductFeature;
window.requireMemoraLearnMode = requireMemoraLearnMode;
window.getMemoraLearnModeAccessMessage = getMemoraLearnModeAccessMessage;
window.getMemoraProductFeatureAccessMessage = getMemoraProductFeatureAccessMessage;

/* --- 2. Call-through stubs ---------------------------------------------- */

const MEMORA_LAZY_STUBS = new Map();
let memoraAskOpenPromise = null;
const MEMORA_ASK_LAZY_PENDING_ID = 'ask-ai-lazy-pending';

function getMemoraAskLazyPendingRefs() {
    const modal = document.getElementById('ask-ai-modal');
    if (!modal) return null;
    let shell = document.getElementById(MEMORA_ASK_LAZY_PENDING_ID);
    if (!shell) {
        shell = document.createElement('div');
        shell.id = MEMORA_ASK_LAZY_PENDING_ID;
        shell.className = 'ask-ai-lazy-pending';
        shell.setAttribute('role', 'status');
        shell.setAttribute('aria-live', 'polite');
        modal.appendChild(shell);
    }
    return { modal, shell };
}

function setMemoraAskLazyPendingState(state = 'loading', message = '') {
    const refs = getMemoraAskLazyPendingRefs();
    if (!refs) return false;
    const { modal, shell } = refs;
    const loading = state === 'loading';
    const trigger = document.getElementById('ask-memora-inline-btn');
    modal.classList.add('active', 'is-feature-pending');
    document.body?.classList?.add('ask-ai-surface-front');
    modal.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (trigger) {
        trigger.disabled = loading;
        trigger.setAttribute('aria-disabled', loading ? 'true' : 'false');
    }
    if (loading) modal.dataset.askHydrating = 'true';
    else delete modal.dataset.askHydrating;
    shell.replaceChildren();
    if (loading) {
        shell.textContent = 'Opening Ask Memoraful…';
        return true;
    }
    const status = document.createElement('p');
    status.textContent = message || 'Ask Memoraful could not load.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'glass-btn';
    retry.textContent = 'Retry Ask Memoraful';
    retry.addEventListener('click', () => {
        if (typeof window.openAskAIModal === 'function') void window.openAskAIModal('');
    });
    shell.append(status, retry);
    return true;
}

function clearMemoraAskLazyPendingState() {
    const modal = document.getElementById('ask-ai-modal');
    const shell = document.getElementById(MEMORA_ASK_LAZY_PENDING_ID);
    const trigger = document.getElementById('ask-memora-inline-btn');
    shell?.remove();
    modal?.classList?.remove('is-feature-pending');
    modal?.removeAttribute?.('aria-busy');
    if (trigger) {
        trigger.disabled = false;
        trigger.setAttribute('aria-disabled', 'false');
    }
    if (modal?.dataset) delete modal.dataset.askHydrating;
    return true;
}

/* Entry points that should pull their module in and then run. Grouped by the
   feature that owns them. */
const MEMORA_LAZY_ENTRY_POINTS = Object.freeze({
    ai: [
        'openAskAIModal',
        'openAskAIModalFromSearch',
        'askMemoraFromQuizAction',
        'openAskMemoraDuoMode',
        'openAskMemoraInAiPane',
        'handleAskMemoraSendButtonClick',
        'openAPIKeyModal',
        'saveGeminiAPIKey',
        'callGeminiFlash',
        'openSlideDigestPreviewModal',
        'onSlideDigestPdfSelectChanged',
        'triggerSlideDigestGenerationForSelectedPdf',
        'copySlideDigestJson',
        'copySlideDigestMarkdown',
        'openMemoraTestInspector',
        'verifyMemoraTestPassword',
        'clearMemoraTestLogs',
        'copyMemoraTestJson'
    ],
    quizgen: [
        'handleImportOrGenerate',
        'showQuizGenerationModal',
        'handleImportFiles',
        'continueToQuizGenerationFromImport',
        'addMorePdfsToQuizGen',
        'startUnifiedGenerationWithMode',
        'toggleQuizGenGenerateMenu',
        'toggleQuizGenPromptPanel',
        'toggleQuizGenUsagePanel',
        'showGeneratedQuizzesPanel',
        'resumeInterruptedQuizGen',
        'discardInterruptedQuizGen',
        'toggleManualImportPanel',
        'startManualImport',
        'processManualJSON',
        'resetManualImport',
        'showManualImportHelp',
        'showRecentlyDeleted',
        'softDeleteSession',
        'checkServerAvailable',
        'showServerRequiredPopup',
        'triggerSmartConnection'
    ],
    regenerate: [
        'regenerateCurrentQuizQuestion'
    ]
});

/* Teardown actions. Nothing can be open before its module has loaded, so these
   stay silent instead of pulling a module in to close a surface that does not
   exist yet. */
const MEMORA_LAZY_NOOP_ENTRY_POINTS = Object.freeze([
    'closeAskAIModal',
    'closeAPIKeyModal',
    'closeSlideDigestPreviewModal',
    'closeMemoraTestInspector',
    'closeMemoraTestAuthModal',
    'closeImportModal',
    'handleQuizGenClose',
    'minimizeQuizGenModal'
]);

/* Synchronous call sites in the eager bundle that are not guarded and expect a
   value or a UI side effect. Each fallback is the correct answer for "this
   feature is not loaded", not a placeholder. */
const MEMORA_LAZY_SYNC_FALLBACKS = Object.freeze({
    // No AI response can be cached before Ask Memora has loaded.
    getAICacheKey: () => '',
    closeAIResponsePopup: () => { },
    restoreAIContent: () => { },
    // The header import button keeps its markup state until generation loads.
    updateImportButtonState: () => { },
    // Smart-line listeners are attached by the generation pane, so there is
    // nothing to detach before it exists.
    detachSmartLineListeners: () => { }
});

function isMemoraLazyStub(value, symbolName) {
    return typeof value !== 'function' || value === MEMORA_LAZY_STUBS.get(symbolName);
}

function reportMemoraFeatureFailure(featureName, symbolName, error) {
    console.error(`Memora could not load the "${featureName}" module for ${symbolName}():`, error);
    if (typeof showPopup === 'function') {
        showPopup('That part of Memoraful is still loading. Check your connection and try again.', null, true);
    }
}

function defineMemoraLazyEntryPoint(featureName, symbolName) {
    if (typeof window[symbolName] === 'function' && !MEMORA_LAZY_STUBS.has(symbolName)) return;
    const stub = function memoraLazyEntryPoint(...args) {
        if (symbolName === 'openAskAIModal') {
            if (memoraAskOpenPromise) return memoraAskOpenPromise;
            setMemoraAskLazyPendingState('loading');
            const openPromise = window.MemoraRuntime.ensureFeature(featureName)
                .then(() => {
                    const implementation = window[symbolName];
                    if (isMemoraLazyStub(implementation, symbolName)) {
                        throw new Error(`"${symbolName}" was not defined by the ${featureName} module.`);
                    }
                    return implementation(...args);
                })
                .then((result) => {
                    clearMemoraAskLazyPendingState();
                    return result;
                })
                .catch((error) => {
                    setMemoraAskLazyPendingState('error', 'Ask Memoraful could not load. Check your connection and try again.');
                    reportMemoraFeatureFailure(featureName, symbolName, error);
                    return undefined;
                })
                .finally(() => {
                    memoraAskOpenPromise = null;
                });
            memoraAskOpenPromise = openPromise;
            return openPromise;
        }
        return window.MemoraRuntime.ensureFeature(featureName)
            .then(() => {
                const implementation = window[symbolName];
                if (isMemoraLazyStub(implementation, symbolName)) {
                    throw new Error(`"${symbolName}" was not defined by the ${featureName} module.`);
                }
                return implementation(...args);
            })
            .catch((error) => {
                reportMemoraFeatureFailure(featureName, symbolName, error);
                return undefined;
            });
    };
    MEMORA_LAZY_STUBS.set(symbolName, stub);
    window[symbolName] = stub;
}

function defineMemoraLazyStubs() {
    Object.entries(MEMORA_LAZY_ENTRY_POINTS).forEach(([featureName, symbolNames]) => {
        symbolNames.forEach((symbolName) => defineMemoraLazyEntryPoint(featureName, symbolName));
    });
    MEMORA_LAZY_NOOP_ENTRY_POINTS.forEach((symbolName) => {
        if (typeof window[symbolName] === 'function' && !MEMORA_LAZY_STUBS.has(symbolName)) return;
        const stub = function memoraLazyNoop() { };
        MEMORA_LAZY_STUBS.set(symbolName, stub);
        window[symbolName] = stub;
    });
    Object.entries(MEMORA_LAZY_SYNC_FALLBACKS).forEach(([symbolName, fallback]) => {
        if (typeof window[symbolName] === 'function' && !MEMORA_LAZY_STUBS.has(symbolName)) return;
        MEMORA_LAZY_STUBS.set(symbolName, fallback);
        window[symbolName] = fallback;
    });
}

defineMemoraLazyStubs();

/* --- 3. Intent triggers and prefetch ------------------------------------- */

const MEMORA_FEATURE_PREFETCH_IDLE_TIMEOUT_MS = 4000;
let memoraFeaturePrefetchScheduled = false;

function ensureMemoraFeature(featureName) {
    return window.MemoraRuntime.ensureFeature(featureName)
        .catch((error) => {
            reportMemoraFeatureFailure(featureName, featureName, error);
            return false;
        });
}
window.ensureMemoraFeature = ensureMemoraFeature;

/* The native file picker must be opened during the activating click. A lazy
   feature stub cannot await module loading first: browsers then treat the
   eventual input.click() as an untrusted call and suppress the chooser. Keep
   this helper eager and deliberately independent of Turnstile/session state;
   the existing universal-import change handler still owns validation and the
   protected upload/generation boundary. */
function openMemoraImportPickerSynchronously() {
    const input = document.getElementById('universal-import');
    if (!input) return false;
    input.dataset.memoraSkipAutoPdfOpen = 'true';
    try {
        input.click();
        return true;
    } catch (error) {
        console.warn('Memora Import file picker is unavailable:', error);
        return false;
    }
}

function bindMemoraImportTrigger() {
    const button = document.getElementById('header-import-btn');
    if (!button || button.dataset.memoraImportTriggerBound === '1') return;
    button.dataset.memoraImportTriggerBound = '1';

    button.addEventListener('click', (event) => {
        const quizGenLoaded = typeof window.MemoraRuntime?.isFeatureLoaded === 'function'
            && window.MemoraRuntime.isFeatureLoaded('quizgen');
        const implementation = window.handleImportOrGenerate;
        if (quizGenLoaded
            && typeof implementation === 'function'
            && !isMemoraLazyStub(implementation, 'handleImportOrGenerate')) {
            implementation();
            return;
        }

        // Phone keeps its existing modal route. Desktop/iPad must consume the
        // activation synchronously so Turnstile or lazy-module readiness can
        // never suppress the local chooser.
        const isPhone = document.documentElement?.classList?.contains('phone-device');
        const isEmbeddedDemo = typeof IS_EMBED_DEMO !== 'undefined' && IS_EMBED_DEMO;
        const hasSessionPdfs = typeof sessionManifest !== 'undefined'
            && Array.isArray(sessionManifest?.pdfs)
            && sessionManifest.pdfs.length > 0;
        if (!isPhone && !isEmbeddedDemo && !hasSessionPdfs && openMemoraImportPickerSynchronously()) {
            void ensureMemoraFeature('quizgen');
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        void ensureMemoraFeature('quizgen').then((loaded) => {
            const nextImplementation = window.handleImportOrGenerate;
            if (loaded
                && typeof nextImplementation === 'function'
                && !isMemoraLazyStub(nextImplementation, 'handleImportOrGenerate')) {
                nextImplementation();
            }
        });
    });
}

/* A surface whose click listener lives inside its own module cannot respond to
   the click that loads it, so the trigger runs the module's entry point once
   the load resolves. It removes itself afterwards and leaves every later click
   to the module's own listener. */
/* `isAllowed` runs before the feature loads: a refused click neither downloads the module nor
   reaches its own listeners. */
function bindMemoraFeatureTrigger(elementId, featureName, run, isAllowed = null) {
    const element = document.getElementById(elementId);
    if (!element || element.dataset.memoraFeatureTrigger === featureName) return;
    element.dataset.memoraFeatureTrigger = featureName;
    let loading = false;
    const handler = async (event) => {
        if (typeof isAllowed === 'function' && !isAllowed()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (loading) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (window.MemoraRuntime.isFeatureLoaded(featureName)) {
            element.removeEventListener('click', handler, true);
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        loading = true;
        const loaded = await ensureMemoraFeature(featureName);
        loading = false;
        if (loaded) {
            element.removeEventListener('click', handler, true);
            if (typeof run === 'function') run();
        }
    };
    element.addEventListener('click', handler, true);
}

function bindMemoraFeatureTriggers() {
    bindMemoraImportTrigger();
    bindMemoraFeatureTrigger('header-clinic-btn', 'clinic', () => { void window.openClinicModal?.(); },
        () => requireMemoraProductFeature('clinic'));
    bindMemoraFeatureTrigger('calendar-toggle-btn', 'calendar', () => { window.toggleCalendarSurface?.({ explicit: true }); });
    bindMemoraFeatureTrigger('welcome-calendar-btn', 'calendar', () => { window.toggleCalendarSurface?.({ explicit: true }); });

    // Calendar owns the phone-surface visibility of its own entry points. Match
    // that decision here so the frozen phone layout does not show a control the
    // Calendar module would have hidden.
    if (document.documentElement.classList.contains('phone-device')) {
        ['calendar-toggle-btn', 'welcome-calendar-btn'].forEach((id) => {
            const button = document.getElementById(id);
            if (button) button.hidden = true;
        });
    }

    // Shift+R is owned by the regeneration module. Cover the shortcut until it
    // loads; the module's own listener takes over from then on.
    document.addEventListener('keydown', (event) => {
        if (window.MemoraRuntime.isFeatureLoaded('regenerate')) return;
        if (event.defaultPrevented || event.isComposing) return;
        if (!event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
        if (String(event.key || '').toLowerCase() !== 'r') return;
        const tag = event.target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
        if (typeof window.shouldSuppressMemoraGlobalShortcuts === 'function'
            && window.shouldSuppressMemoraGlobalShortcuts(event)) return;
        event.preventDefault();
        void window.regenerateCurrentQuizQuestion?.();
    });
}

/* index.html sets calendar-auto-open-pending from the saved launch preference
   and Calendar clears it. That class hides the workspace, so the module has to
   load now rather than on intent. */
function ensureMemoraCalendarAutoOpen() {
    if (!document.documentElement.classList.contains('calendar-auto-open-pending')) return;
    void ensureMemoraFeature('calendar');
}

/* Desktop/iPad warms only an intended feature. Phone retains its existing idle
   loading policy. Click entry points still own activation and await readiness. */
function scheduleMemoraFeaturePrefetch() {
    if (memoraFeaturePrefetchScheduled) return;
    memoraFeaturePrefetchScheduled = true;
    if (navigator.connection?.saveData) return;
    // Smoke mode asserts what loads on intent, so it opts out of the prefetch.
    if (window.__MEMORA_SMOKE_MODE__ || window.__MEMORA_DISABLE_FEATURE_PREFETCH__) return;
    if (!document.documentElement.classList.contains('phone-device')) {
        if (['slow-2g', '2g'].includes(navigator.connection?.effectiveType)) return;
        const targets = [
            ['#header-import-btn, [data-live-rail-action="add-material"]', 'quizgen'],
            ['#header-tutor-btn, #ask-memora-inline-btn, [data-live-rail-action="learn"], [data-live-plan-action="learn-guide"], [data-live-plan-action="learn-flashcards"], [data-live-plan-action="learn-occlusion"], [data-live-plan-action="learn-guided"]', 'ai'],
            ['#header-clinic-btn, [data-live-rail-action="clinic"], [data-live-plan-action="learn-clinic"]', 'clinic'],
            ['#calendar-toggle-btn, #welcome-calendar-btn, [data-live-rail-action="calendar"]', 'calendar']
        ];
        let pendingTarget = null;
        let timer = 0;
        const cancel = () => {
            clearTimeout(timer);
            timer = 0;
            pendingTarget = null;
        };
        const warm = (event) => {
            if (event.type === 'pointerover' && event.pointerType === 'touch') return;
            if (event.type === 'pointerdown' && event.pointerType !== 'touch') return;
            if (document.visibilityState === 'hidden' || navigator.connection?.saveData) return;
            for (const [selector, featureName] of targets) {
                const target = event.target?.closest?.(selector);
                if (!target || target.disabled || target.closest('[inert], [hidden]')) continue;
                if (target.contains(event.relatedTarget)) return;
                if (window.MemoraRuntime.isFeatureLoaded(featureName)) return;
                cancel();
                pendingTarget = target;
                timer = setTimeout(() => {
                    cancel();
                    if (!target.isConnected || document.visibilityState === 'hidden') return;
                    void window.MemoraRuntime.ensureFeature(featureName).catch((error) => {
                        // A speculative failure must not show a popup or disable click retry.
                        console.warn(`Prefetch of the "${featureName}" module did not complete:`, error);
                    });
                }, event.type === 'pointerover' ? 120 : 0);
                return;
            }
        };
        const leave = (event) => {
            if (pendingTarget?.contains(event.target) && !pendingTarget.contains(event.relatedTarget)) cancel();
        };
        document.addEventListener('pointerover', warm, { passive: true });
        document.addEventListener('pointerdown', warm, { passive: true });
        document.addEventListener('focusin', warm);
        document.addEventListener('pointerout', leave, { passive: true });
        document.addEventListener('focusout', leave);
        return;
    }
    const prefetch = async () => {
        for (const featureName of ['ai', 'quizgen']) {
            if (window.MemoraRuntime.isFeatureLoaded(featureName)) continue;
            try {
                await window.MemoraRuntime.ensureFeature(featureName);
            } catch (error) {
                console.warn(`Prefetch of the "${featureName}" module did not complete:`, error);
                return;
            }
        }
    };
    const start = () => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => { void prefetch(); }, { timeout: MEMORA_FEATURE_PREFETCH_IDLE_TIMEOUT_MS });
            return;
        }
        setTimeout(() => { void prefetch(); }, 1500);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
}

/* The header import button reflects generation state that only the generation
   module knows about, so refresh it the moment that module arrives. */
window.MemoraRuntime.onFeatureLoaded((featureName) => {
    if (featureName !== 'quizgen') return;
    if (typeof updateImportButtonState === 'function') updateImportButtonState();
});

window.MemoraRuntime.whenDocumentReady(() => {
    bindMemoraFeatureTriggers();
    ensureMemoraCalendarAutoOpen();
    scheduleMemoraFeaturePrefetch();
});
